import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Upload, Download, Calculator } from 'lucide-react';
import { AppShell } from '@/components/layout';
import { DocumentsTab, DownloadsTab, CalculatorTab, LiveVoiceTab } from '@/components/assistant';
import { cn } from '@/lib/cn';
import { staggerContainer, staggerItem } from '@/lib/motion';

type TabType = 'voice' | 'upload' | 'downloads' | 'calculator';

interface Tab {
  id: TabType;
  name: string;
  icon: React.ElementType;
  description: string;
}

const tabs: Tab[] = [
  {
    id: 'voice',
    name: 'Voice',
    icon: Mic,
    description: 'Talk to DryVox',
  },
  {
    id: 'upload',
    name: 'Upload',
    icon: Upload,
    description: 'Context documents',
  },
  {
    id: 'downloads',
    name: 'Downloads',
    icon: Download,
    description: 'Generated files',
  },
  {
    id: 'calculator',
    name: 'Calculator',
    icon: Calculator,
    description: 'Shipping tools',
  },
];

/**
 * Assistant Hub - Multi-feature AI assistant interface
 *
 * Features:
 * - Voice: Real-time voice interaction
 * - Upload: Upload context documents for the AI
 * - Downloads: Generated documents and images from voice sessions
 * - Calculator: Laytime, demurrage, distance tools
 */
const VALID_TABS: TabType[] = ['voice', 'upload', 'downloads', 'calculator'];

export function VoiceAssistant() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<TabType>(() =>
    tabFromUrl && VALID_TABS.includes(tabFromUrl as TabType) ? (tabFromUrl as TabType) : 'voice'
  );

  // Sync URL → state when the URL changes (e.g. deep link or back/forward)
  useEffect(() => {
    if (tabFromUrl && VALID_TABS.includes(tabFromUrl as TabType)) {
      setActiveTab(tabFromUrl as TabType);
    }
  }, [tabFromUrl]);

  // Sync state → URL when the user clicks a tab
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    // Update URL without full navigation; use 'voice' default silently
    if (tab === 'voice') {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab }, { replace: true });
    }
  };

  return (
    <AppShell>
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        className="space-y-6"
      >
        {/* Page Header */}
        <motion.div variants={staggerItem}>
          <h1 className="text-2xl md:text-3xl font-display font-bold text-white">
            Assistant Hub
          </h1>
          <p className="text-gray-400 mt-1">
            Your AI-powered maritime assistant with multiple capabilities
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div variants={staggerItem}>
          <div className="flex flex-wrap gap-2 p-1 rounded-2xl bg-white/5 border border-white/10">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={cn(
                    'relative flex items-center gap-2 px-4 py-3 rounded-xl transition-all flex-1 min-w-[120px]',
                    isActive
                      ? 'text-white'
                      : 'text-white/50 hover:text-white/70'
                  )}
                >
                  {isActive && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute inset-0 bg-gradient-to-r from-ocean-cyan/30 to-ocean-blue/30 rounded-xl border border-ocean-cyan/30"
                      transition={{ type: 'spring', duration: 0.5 }}
                    />
                  )}
                  <Icon className={cn(
                    'w-5 h-5 relative z-10',
                    isActive && 'text-ocean-cyan'
                  )} />
                  <div className="relative z-10 text-left hidden sm:block">
                    <p className="font-medium text-sm">{tab.name}</p>
                    <p className="text-xs text-white/40">{tab.description}</p>
                  </div>
                  <span className="relative z-10 sm:hidden font-medium text-sm">
                    {tab.name}
                  </span>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Tab Content */}
        <motion.div variants={staggerItem}>
          {/*
           * IMPORTANT: LiveVoiceTab is ALWAYS mounted so the WebSocket connection
           * persists across tab switches. We use CSS display to hide/show it instead
           * of conditional rendering. Other tabs mount/unmount normally.
           */}
          <div style={{ display: activeTab === 'voice' ? 'block' : 'none' }}>
            <LiveVoiceTab />
          </div>

          <AnimatePresence mode="wait">
            {activeTab !== 'voice' && (
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {activeTab === 'upload' && <DocumentsTab />}
                {activeTab === 'downloads' && <DownloadsTab />}
                {activeTab === 'calculator' && <CalculatorTab />}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </AppShell>
  );
}

export default VoiceAssistant;
